import { useEffect } from 'react';
import { AlertTriangle, Play, Square, RotateCcw, LogOut } from 'lucide-react';
import { GuadalajaraMapView } from './views/GuadalajaraMapView';
import { useDetailedSimulation } from './store/detailedSimulation';
import { useFrameStore } from './store/frameStore';
import { useHeaderKPIs } from './components/guadalajara/KPIDashboard';
import { useAlarmSimulation } from './components/guadalajara/AlarmPanel';
import { HealthBadge } from './components/guadalajara/HealthBadge';
import { useDemoTourRunner } from './hooks/useDemoTour';
import { useDemoTour } from './store/demoTour';
import { RequireAuth } from './components/RequireAuth';
import { useAuth } from './hooks/useAuth';

function AppContent() {
  const isRunning = useDetailedSimulation(s => s.isRunning);
  const start = useDetailedSimulation(s => s.start);
  const stop = useDetailedSimulation(s => s.stop);
  const generateMockFrames = useFrameStore(s => s.generateMockFrames);
  const hasFrames = useFrameStore(s => s.frames.length > 0);
  const user = useAuth(s => s.user);
  const logout = useAuth(s => s.logout);

  // Auto-start simulation
  useEffect(() => {
    if (!isRunning) {
      if (!hasFrames) generateMockFrames();
      start();
    }
    return () => stop();
  }, []);

  const { regulators, globalHealth, avgAvailability } = useHeaderKPIs();
  const { alarms, addAlarm, resolveAlarm, cleanResolved } = useAlarmSimulation();
  const { touring, run: runTour, cancel: cancelTour } = useDemoTourRunner(addAlarm);
  const alarmLoopActive = useDemoTour(s => s.alarmLoopActive);
  // "completed" = tour finished, alarm loop running
  const tourCompleted = !touring && alarmLoopActive;

  return (
    <div className="h-screen bg-gray-950 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 px-3 py-2 flex-shrink-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-ars93.jpg" alt="ARS_93" className="h-8 rounded" />
            <div>
              <h1 className="text-sm font-semibold text-white leading-tight">ARS_93</h1>
              <p className="text-[10px] text-gray-500">{user?.org_name || 'Guadalajara'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* KPI chips — one per regulator */}
            <div data-highlight="header-kpis" className="hidden sm:flex items-center gap-2 px-2 py-1.5 bg-gray-800/60 rounded-lg">
              {regulators.map(r => (
                <div key={r.id} className="flex items-center gap-1">
                  <HealthBadge status={r.health} size={6} />
                  <span className="text-[10px] font-mono text-gray-300">R{r.id}</span>
                  <span className="text-[10px] font-mono text-gray-500">{r.availability.toFixed(1)}%</span>
                </div>
              ))}
            </div>

            {/* Mobile: global health only */}
            <div className="sm:hidden flex items-center gap-1.5 px-2 py-1.5 bg-gray-800/60 rounded-lg">
              <HealthBadge status={globalHealth} size={7} />
              <span className="text-[11px] font-mono text-gray-300">{avgAvailability.toFixed(1)}%</span>
            </div>

            {/* Demo tour button */}
            <button
              onClick={touring ? cancelTour : runTour}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
                touring
                  ? 'bg-red-900/40 border-red-600/50 text-red-400 hover:bg-red-900/60'
                  : tourCompleted
                    ? 'bg-gray-800/60 border-gray-600/50 text-gray-300 hover:bg-gray-700/60'
                    : 'bg-cyan-900/40 border-cyan-600/50 text-cyan-400 hover:bg-cyan-900/60'
              }`}
            >
              {touring ? (
                <><Square className="w-3 h-3" /><span className="hidden sm:inline">Parar</span></>
              ) : tourCompleted ? (
                <><RotateCcw className="w-3 h-3" /><span className="hidden sm:inline">Repetir</span></>
              ) : (
                <><Play className="w-3 h-3" /><span className="hidden sm:inline">Demo</span></>
              )}
            </button>

            {/* Alarm count (active only) */}
            {(() => {
              const activeCount = alarms.filter(a => !a.resolvedAt).length;
              return (
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${activeCount > 0 ? 'bg-red-900/30' : 'bg-gray-800/60'}`}>
                  <AlertTriangle className={`w-3 h-3 ${activeCount > 0 ? 'text-red-400' : 'text-gray-600'}`} />
                  <span className={`text-[11px] font-mono font-medium ${activeCount > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                    {activeCount}
                  </span>
                </div>
              );
            })()}

            {/* User + logout */}
            {user && (
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-800/60 rounded-lg hover:bg-gray-700/60 transition-colors"
                title={`${user.email} — Cerrar sesión`}
              >
                <span className="text-[10px] font-mono text-gray-400 hidden sm:inline">{user.email}</span>
                <LogOut className="w-3 h-3 text-gray-500" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Map fullscreen */}
      <main className="flex-1 overflow-hidden">
        <GuadalajaraMapView alarms={alarms} addAlarm={addAlarm} resolveAlarm={resolveAlarm} cleanResolved={cleanResolved} />
      </main>
    </div>
  );
}

function App() {
  return (
    <RequireAuth>
      <AppContent />
    </RequireAuth>
  );
}

export default App;
